import { type ReactElement } from "react";

import { css, cx } from "@emotion/css";
import { BorderRadius, Colors, FontSizeInPx, FontWeight, LineHeightInPx, Spacing } from "@snapchat/snap-design-system";
import { CheckCircleIcon } from "@snapchat/snap-design-system-icons";
import { type OptionProps } from "react-select";

import { type Option } from "../models";

const optionContainer = css`
    cursor: pointer;

    display: grid;
    grid-template-columns: 100% auto 30px;
    align-items: center;

    height: 32px;

    border-radius: ${BorderRadius.container}px;
`;

const optionFocused = css`
    background: ${Colors.Gray.V50};
`;

const optionDisabled = css`
    cursor: not-allowed;
    opacity: 0.5;
`;

const labelContainer = css`
    display: flex;
    align-items: center;
    justify-content: space-between;

    width: 100%;
    padding: ${Spacing.XS}px;

    font-size: ${FontSizeInPx.COMPACT}px;
    font-weight: ${FontWeight.NORMAL};
    line-height: ${LineHeightInPx.REGULAR}px;
    color: ${Colors.Black.V50};
`;

const labelSelected = css`
    color: ${Colors.Black.V200};
`;

export const SelectOption = <T,>({
    isFocused,
    isDisabled,
    isSelected,
    innerProps,
    innerRef,
    data: { label },
}: OptionProps<Option<T>>): ReactElement => {
    const optionClassName = cx(optionContainer, isFocused && optionFocused, isDisabled && optionDisabled);
    return (
        <div data-testid={`option-${label}`} className={optionClassName} ref={innerRef} {...innerProps}>
            <span className={cx(labelContainer, isSelected && labelSelected)}>
                {label}
                {isSelected && <CheckCircleIcon fill={Colors.Green.V150} />}
            </span>
        </div>
    );
};
